import { IUserServer, SUBSCRIPTION_TYPE } from "../context/types";
import type { IStyles } from "../context/stylesContext";
import { getAmountDaysLeft } from "../utils/dayLeft";

/**
 * Ключи фонов — PNG 1000×1000 в `public/images/backgrounds/`.
 * В AppBackground слой всегда 1000×1000, отображение через `background-size: cover`
 * (не contain), на узком экране обрезка по краям, без сжатия всего изображения.
 */
export type HomeBackgroundKey = "home-base" | "home-warning" | "home-fail" | "settings";

export const HOME_BACKGROUNDS: Record<HomeBackgroundKey, string> = {
  "home-base": "/images/backgrounds/home-base.png",
  "home-warning": "/images/backgrounds/home-warning.png",
  "home-fail": "/images/backgrounds/home-fail.png",
  "settings": "/images/backgrounds/settings.png",
};

export const DEFAULT_BACKGROUND_IMAGE = HOME_BACKGROUNDS["home-base"];

/**
 * Какой PNG показать на главной в зависимости от подписки.
 * Раньше это было `filter: grayscale(...)` на SVG — теперь отдельные картинки.
 */
export function getHomeBackgroundImage(
  userServer: IUserServer | null | undefined
): string {
  if (!userServer) {
    return HOME_BACKGROUNDS["home-base"];
  }

  if (!userServer.isEnabled) {
    return HOME_BACKGROUNDS["home-fail"];
  }

  const expired = new Date(userServer.subscriptionEndDate) < new Date();

  if (userServer.subscriptionType === SUBSCRIPTION_TYPE.TEST) {
    if (expired) return HOME_BACKGROUNDS["home-fail"];
    const dayLeft = getAmountDaysLeft(userServer.subscriptionEndDate);
    if (dayLeft === 1) return HOME_BACKGROUNDS["home-warning"];
    return HOME_BACKGROUNDS["home-base"];
  }

  if (expired) {
    return HOME_BACKGROUNDS["home-fail"];
  }

  const dayLeft = getAmountDaysLeft(userServer.subscriptionEndDate);
  if (dayLeft <= 3) {
    return HOME_BACKGROUNDS["home-warning"];
  }

  return HOME_BACKGROUNDS["home-base"];
}

const FAIL_ROUTES = new Set([
  "/payment/fail",
  "/device-fail",
  "/subscription-fail",
]);

/** Страницы с чёрной/скролл-обёрткой — глобальное свечение прячем. */
const HIDDEN_GLOW_ROUTES = new Set([
  "/instruction",
  // "/privacy-policy",
  // "/user-agreement",
  // "/agreement",
  // "/offer",
]);

const SETTINGS_ROUTES = new Set(["/settings"]);

/** Экран выбора тарифа — свечение поднимаем над нижней панелью (фиксированно, без привязки к высоте модалки). */
const SELECT_TARIFF_ROUTES = new Set(["/select-tariff"]);

const DIMMED_ROUTES = new Set([
  "/settings-end",
  "/account/login-methods/link-email/success",
  "/error",
]);

/**
 * Единая точка: какой фон показать для текущего URL.
 * Вызывается при каждой смене маршрута (см. BackgroundSync).
 */
export function resolveRouteBackground(
  pathname: string,
  userServer: IUserServer | null | undefined
): IStyles {
  if (pathname === "/") {
    return {
      backgroundImage: getHomeBackgroundImage(userServer),
      bottom: "0",
      opacity: "1",
    };
  }

  if (SETTINGS_ROUTES.has(pathname)) {
    return {
      backgroundImage: HOME_BACKGROUNDS["settings"],
      bottom: "0",
      opacity: "1",
    };
  }

  if (SELECT_TARIFF_ROUTES.has(pathname)) {
    return {
      backgroundImage: DEFAULT_BACKGROUND_IMAGE,
      // Поднимаем свечение над панелью тарифов; значение стабильное — без прыжков при ресайзе листа.
      bottom: "220px",
      opacity: "1",
    };
  }

  if (FAIL_ROUTES.has(pathname)) {
    return {
      backgroundImage: HOME_BACKGROUNDS["home-fail"],
      bottom: "0",
      opacity: "1",
    };
  }

  if (HIDDEN_GLOW_ROUTES.has(pathname)) {
    return {
      backgroundImage: DEFAULT_BACKGROUND_IMAGE,
      bottom: "0",
      opacity: "0",
    };
  }

  if (DIMMED_ROUTES.has(pathname)) {
    return {
      backgroundImage: DEFAULT_BACKGROUND_IMAGE,
      bottom: "15%",
      opacity: "0.5",
    };
  }

  return {
    backgroundImage: DEFAULT_BACKGROUND_IMAGE,
    bottom: "0",
    opacity: "1",
  };
}
